package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.models.Patient
import com.example.borshi.web.nutritionist.models.PatientStatus
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Edit Patient Modal for updating existing patient records
 * Connects to Firebase 'users' collection
 */
@Composable
fun EditPatientModal(
    patient: Patient,
    repository: NutritionistRepository,
    onClose: () -> Unit,
    onPatientUpdated: () -> Unit
) {
    var name by remember { mutableStateOf(patient.name) }
    var email by remember { mutableStateOf(patient.email) }
    var phone by remember { mutableStateOf(patient.phone) }
    var age by remember { mutableStateOf(patient.age.toString()) }
    var gender by remember { mutableStateOf(patient.gender) }
    var height by remember { mutableStateOf(patient.height.toString()) }
    var weight by remember { mutableStateOf(patient.weight.toString()) }
    var healthGoals by remember { mutableStateOf(patient.healthGoals.joinToString(", ")) }
    var healthConditions by remember { mutableStateOf(patient.healthConditions.joinToString(", ")) }
    var allergies by remember { mutableStateOf(patient.allergies.joinToString(", ")) }
    var dietaryRestrictions by remember { mutableStateOf(patient.dietaryRestrictions.joinToString(", ")) }
    var status by remember { mutableStateOf(patient.status.name) }

    var isSubmitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Handler for form submission
    val handleSubmit: () -> Unit = {
        scope.launch {
            try {
                isSubmitting = true
                error = null

                // Validate required fields
                if (name.isBlank() || email.isBlank() || phone.isBlank()) {
                    error = "Name, email, and phone are required"
                    isSubmitting = false
                    return@launch
                }

                val ageInt = age.toIntOrNull() ?: patient.age
                val heightDouble = height.toDoubleOrNull() ?: patient.height
                val weightDouble = weight.toDoubleOrNull() ?: patient.weight

                // Calculate BMI
                val bmi = if (heightDouble > 0 && weightDouble > 0) {
                    weightDouble / ((heightDouble / 100.0) * (heightDouble / 100.0))
                } else {
                    patient.bmi
                }

                // Parse lists from comma-separated strings
                val healthGoalsList = healthGoals.split(",").map { it.trim() }.filter { it.isNotBlank() }
                val healthConditionsList = healthConditions.split(",").map { it.trim() }.filter { it.isNotBlank() }
                val allergiesList = allergies.split(",").map { it.trim() }.filter { it.isNotBlank() }
                val dietaryRestrictionsList = dietaryRestrictions.split(",").map { it.trim() }.filter { it.isNotBlank() }

                // Create updated patient object
                val updatedPatient = patient.copy(
                    name = name.trim(),
                    email = email.trim(),
                    phone = phone.trim(),
                    age = ageInt,
                    gender = gender,
                    height = heightDouble,
                    weight = weightDouble,
                    bmi = bmi,
                    healthGoals = healthGoalsList,
                    healthConditions = healthConditionsList,
                    allergies = allergiesList,
                    dietaryRestrictions = dietaryRestrictionsList,
                    status = when(status) {
                        "ACTIVE" -> PatientStatus.ACTIVE
                        "INACTIVE" -> PatientStatus.INACTIVE
                        else -> PatientStatus.ACTIVE
                    },
                    updatedAt = js("Date.now()") as Long
                )

                // Update in repository
                repository.updatePatient(updatedPatient)
                    .onSuccess {
                        console.log("✅ Patient updated successfully")
                        isSubmitting = false
                        onPatientUpdated()
                    }
                    .onFailure { ex ->
                        console.error("❌ Error updating patient:", ex)
                        error = "Failed to update patient: ${ex.message}"
                        isSubmitting = false
                    }

            } catch (e: Exception) {
                console.error("❌ Error:", e)
                error = "Error: ${e.message}"
                isSubmitting = false
            }
        }
    }

    // Modal Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        // Modal Content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(600.px)
                width(100.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    padding(20.px)
                    property("border-bottom", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                H2({ style { margin(0.px); fontSize(24.px); color(Color("#333")) } }) {
                    Text("✏️ Edit Patient Profile")
                }
                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        border(0.px)
                        fontSize(24.px)
                        cursor("pointer")
                        padding(0.px)
                        width(32.px)
                        height(32.px)
                    }
                    onClick { onClose() }
                }) {
                    Text("×")
                }
            }

            // Form
            Div({
                style {
                    padding(20.px)
                }
            }) {
                // Error Display
                if (error != null) {
                    ErrorMessage(error!!)
                }

                // Basic Information
                FormSection("Basic Information") {
                    FormField("Name *", "name") {
                        Input(InputType.Text) {
                            value(name)
                            onInput { name = it.value }
                            attr("required", "true")
                            attr("placeholder", "Full name")
                        }
                    }

                    FormField("Email *", "email") {
                        Input(InputType.Email) {
                            value(email)
                            onInput { email = it.value }
                            attr("required", "true")
                            attr("placeholder", "email@example.com")
                        }
                    }

                    FormField("Phone *", "phone") {
                        Input(InputType.Tel) {
                            value(phone)
                            onInput { phone = it.value }
                            attr("required", "true")
                            attr("placeholder", "+880 1XXX-XXXXXX")
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "1fr 1fr")
                            gap(12.px)
                        }
                    }) {
                        FormField("Age *", "age") {
                            Input(InputType.Number) {
                                value(age)
                                onInput { age = it.value?.toString() ?: "" }
                                attr("placeholder", "25")
                                attr("min", "1")
                            }
                        }

                        FormField("Gender *", "gender") {
                            Select({
                                style {
                                    width(100.percent)
                                    padding(10.px)
                                    border(1.px, LineStyle.Solid, Color("#ddd"))
                                    borderRadius(6.px)
                                    fontSize(14.px)
                                }
                                onChange { gender = it.value ?: "" }
                            }) {
                                Option("") { Text("Select gender") }
                                Option("Male", { if (gender == "Male") selected() }) { Text("Male") }
                                Option("Female", { if (gender == "Female") selected() }) { Text("Female") }
                                Option("Other", { if (gender == "Other") selected() }) { Text("Other") }
                            }
                        }
                    }
                }

                // Physical Measurements
                FormSection("Physical Measurements") {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "1fr 1fr")
                            gap(12.px)
                        }
                    }) {
                        FormField("Height (cm)", "height") {
                            Input(InputType.Number) {
                                value(height)
                                onInput { height = it.value?.toString() ?: "" }
                                attr("placeholder", "170")
                                attr("min", "50")
                                attr("step", "0.1")
                            }
                        }

                        FormField("Weight (kg)", "weight") {
                            Input(InputType.Number) {
                                value(weight)
                                onInput { weight = it.value?.toString() ?: "" }
                                attr("placeholder", "70")
                                attr("min", "20")
                                attr("step", "0.1")
                            }
                        }
                    }
                }

                // Health Information
                FormSection("Health Information") {
                    FormField("Health Goals", "healthGoals") {
                        Input(InputType.Text) {
                            value(healthGoals)
                            onInput { healthGoals = it.value }
                            attr("placeholder", "e.g., Weight Loss, Muscle Building")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Health Conditions", "healthConditions") {
                        Input(InputType.Text) {
                            value(healthConditions)
                            onInput { healthConditions = it.value }
                            attr("placeholder", "e.g., Diabetes, Hypertension")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Allergies", "allergies") {
                        Input(InputType.Text) {
                            value(allergies)
                            onInput { allergies = it.value }
                            attr("placeholder", "e.g., Shellfish, Nuts")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }

                    FormField("Dietary Restrictions", "dietaryRestrictions") {
                        Input(InputType.Text) {
                            value(dietaryRestrictions)
                            onInput { dietaryRestrictions = it.value }
                            attr("placeholder", "e.g., Vegetarian, Gluten-free")
                        }
                        Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                            Text("Comma-separated")
                        }
                    }
                }

                // Status
                FormSection("Status") {
                    FormField("Patient Status", "status") {
                        Select({
                            style {
                                width(100.percent)
                                padding(10.px)
                                border(1.px, LineStyle.Solid, Color("#ddd"))
                                borderRadius(6.px)
                                fontSize(14.px)
                            }
                            onChange { status = it.value ?: "" }
                        }) {
                            Option("ACTIVE", { if (status == "ACTIVE") selected() }) { Text("Active") }
                            Option("INACTIVE", { if (status == "INACTIVE") selected() }) { Text("Inactive") }
                        }
                    }
                }

                // Action Buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        justifyContent(JustifyContent.FlexEnd)
                        marginTop(20.px)
                    }
                }) {
                    Button({
                        style {
                            padding(10.px, 20.px)
                            backgroundColor(Color("transparent"))
                            border(1.px, LineStyle.Solid, Color("#ddd"))
                            borderRadius(6.px)
                            cursor("pointer")
                            fontSize(14.px)
                        }
                        if (isSubmitting) {
                            attr("disabled", "disabled")
                        }
                        onClick { onClose() }
                    }) {
                        Text("Cancel")
                    }

                    Button({
                        style {
                            padding(10.px, 20.px)
                            backgroundColor(if (isSubmitting) Color("#ccc") else Color("#2196F3"))
                            color(Color.white)
                            border(0.px)
                            borderRadius(6.px)
                            cursor(if (isSubmitting) "not-allowed" else "pointer")
                            fontSize(14.px)
                            fontWeight("600")
                        }
                        if (isSubmitting) {
                            attr("disabled", "disabled")
                        }
                        onClick {
                            if (!isSubmitting) {
                                handleSubmit()
                            }
                        }
                    }) {
                        Text(if (isSubmitting) "Updating..." else "Update Patient")
                    }
                }
            }
        }
    }
}

